/**
 * Backend Coaching Service
 * 
 * Handles communication with backend API for weekly coaching generation.
 * Includes adapter to convert backend response format to frontend format.
 *
 * FE HARDENING (v2):
 * - Set-based deduplication: no message appears in more than one category.
 * - Simplified exercise extraction: removed 90-line BUG FIX #12 cascade.
 *   Single expanded keyword pass replaces 4 nested fallback layers.
 * - Production logging: verbose console.log replaced with warnings only.
 */

import { env } from '../utils/env';
import { loggedFetch } from '../utils/apiLogger';
import { getFirebaseIdToken } from '../utils/firebaseToken';
import type { WeeklyCoachingResult } from './behaviourEngine.service';

/**
 * Backend API response format
 */
export interface BackendCoachingResponse {
  route: 'cached' | 'text_full' | 'text_mini' | 'fallback';
  result: {
    summary: string;
    recommendations: string[];
    next_actions: string[];
    confidence: 'low' | 'medium' | 'high';
  };
}

/**
 * Fetch weekly coaching from backend API
 */
export async function fetchWeeklyCoachingFromBackend(
  weekStart?: string,
  forceRefresh?: boolean,
): Promise<BackendCoachingResponse> {
  const token = await getFirebaseIdToken();

  const url = `${env.apiBaseUrl}/v1/coaching/weekly`;

  const requestBody: { weekStart?: string; forceRefresh?: boolean } = {};
  if (weekStart) {
    requestBody.weekStart = weekStart;
  }
  if (forceRefresh !== undefined) {
    requestBody.forceRefresh = forceRefresh;
  }

  const response = await loggedFetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(error.message || `HTTP ${response.status}`);
  }

  const result = await response.json() as BackendCoachingResponse;
  return result;
}

// ── Keyword sets (unified — no duplication) ──

const EXERCISE_KEYWORDS = [
  'workout', 'workouts', 'exercise', 'exercises', 'training', 'gym',
  'run', 'runs', 'running', 'cardio', 'strength',
  'activity', 'activities', 'session', 'sessions', 'fitness',
  'muscle', 'muscles', 'train',
  'lift', 'lifting', 'weight', 'weights', 'strength training',
  'aerobic', 'endurance', 'sprint', 'walk', 'walks',
  'step', 'steps', 'active', 'movement', 'physical',
  'burn', 'calories burned', 'epoc',
  'recovery', 'rest day', 'rest days',
];

const NUTRITION_KEYWORDS = [
  'protein', 'calorie', 'nutrition', 'food', 'meal', 'eating', 'diet',
  'macro', 'carb', 'fat', 'fibre', 'hydration', 'water', 'intake',
];

const BODY_STATS_KEYWORDS = [
  'body fat', 'muscle mass', 'scale', 'measurement', 'progress',
  'composition', 'body weight',
];

const BEHAVIOUR_KEYWORDS = [
  'log', 'logging', 'consistency', 'track', 'record', 'habit',
  'routine', 'pattern', 'regular',
];

/**
 * Score a message against a keyword set.
 * Returns the number of keyword matches (not just first-match).
 * Higher score = stronger affinity for that category.
 */
function scoreMessage(message: string, keywords: readonly string[]): number {
  const lower = message.toLowerCase();
  let score = 0;
  for (const keyword of keywords) {
    if (lower.includes(keyword)) score++;
  }
  return score;
}

/**
 * Categorise a message using scoring (highest score wins).
 * Tie-breaking priority: exercise > nutrition > body_stats > behaviour.
 * Returns null if no keywords match at all.
 */
function categorizeMessage(message: string): 'nutrition' | 'exercise' | 'body_stats' | 'behaviour' | null {
  const scores = {
    exercise: scoreMessage(message, EXERCISE_KEYWORDS),
    nutrition: scoreMessage(message, NUTRITION_KEYWORDS),
    body_stats: scoreMessage(message, BODY_STATS_KEYWORDS),
    behaviour: scoreMessage(message, BEHAVIOUR_KEYWORDS),
  };

  const maxScore = Math.max(scores.exercise, scores.nutrition, scores.body_stats, scores.behaviour);
  if (maxScore === 0) return null;

  // Tie-breaking: exercise > nutrition > body_stats > behaviour
  if (scores.exercise === maxScore) return 'exercise';
  if (scores.nutrition === maxScore) return 'nutrition';
  if (scores.body_stats === maxScore) return 'body_stats';
  return 'behaviour';
}

/**
 * Map backend coaching response to frontend WeeklyCoachingResult format.
 *
 * Strategy:
 * 1. Score each message against all keyword sets (highest score wins, ties broken by priority).
 * 2. Track all assigned messages in a Set — no duplicates across categories.
 * 3. Summary lines go to behaviour only if not already assigned.
 * 4. If a category is empty, do a single keyword scan of unassigned messages (no nested fallbacks).
 */
export function mapBackendToFrontendFormat(
  backendResponse: BackendCoachingResponse,
): WeeklyCoachingResult {
  const { result } = backendResponse;
  const frontendResult: WeeklyCoachingResult = {
    behaviour: [],
    nutrition_l2: [],
    body_stats: [],
    exercise: [],
  };

  // ── Deduplication: track all assigned messages ──
  const assigned = new Set<string>();

  function assignMessage(message: string, category: keyof WeeklyCoachingResult): void {
    const trimmed = message.trim();
    if (!trimmed || assigned.has(trimmed)) return;
    assigned.add(trimmed);
    frontendResult[category].push(trimmed);
  }

  // ── Process recommendations + next_actions ──
  const allMessages = [
    ...(result.recommendations || []),
    ...(result.next_actions || []),
  ];

  for (const msg of allMessages) {
    const category = categorizeMessage(msg);
    if (category === 'nutrition') {
      assignMessage(msg, 'nutrition_l2');
    } else if (category === 'exercise') {
      assignMessage(msg, 'exercise');
    } else if (category === 'body_stats') {
      assignMessage(msg, 'body_stats');
    } else {
      assignMessage(msg, 'behaviour');
    }
  }

  // ── Process summary → behaviour (if not already assigned) ──
  if (result.summary && result.summary.trim().length > 0) {
    const summaryLines = result.summary.split(/[.!?]\s+/).filter(line => line.trim().length > 0);
    for (const line of summaryLines.slice(0, 2)) {
      const trimmed = line.trim();
      if (trimmed.length > 0 && trimmed.length < 200) {
        assignMessage(trimmed, 'behaviour');
      }
    }
  }

  // ── Fill empty categories from unassigned messages (single pass, no nested fallbacks) ──
  const unassigned = allMessages.filter(msg => !assigned.has(msg.trim()));

  if (frontendResult.behaviour.length === 0 && unassigned.length > 0) {
    for (const msg of unassigned.slice(0, 2)) {
      assignMessage(msg, 'behaviour');
    }
  }

  if (frontendResult.exercise.length === 0) {
    const exerciseUnassigned = unassigned.filter(msg => scoreMessage(msg, EXERCISE_KEYWORDS) > 0);
    for (const msg of exerciseUnassigned.slice(0, 2)) {
      assignMessage(msg, 'exercise');
    }
    // If still empty after keyword scan, check summary
    if (frontendResult.exercise.length === 0 && result.summary) {
      const summarySentences = result.summary.split(/[.!?]\s+/).filter(s =>
        scoreMessage(s, EXERCISE_KEYWORDS) > 0 && !assigned.has(s.trim()),
      );
      for (const s of summarySentences.slice(0, 2)) {
        assignMessage(s, 'exercise');
      }
    }
  }

  if (frontendResult.nutrition_l2.length === 0) {
    const nutritionUnassigned = unassigned.filter(msg => scoreMessage(msg, NUTRITION_KEYWORDS) > 0);
    for (const msg of nutritionUnassigned.slice(0, 2)) {
      assignMessage(msg, 'nutrition_l2');
    }
  }

  // ── Warn if exercise coaching is still empty (for monitoring) ──
  if (frontendResult.exercise.length === 0) {
    console.warn('[BackendCoaching] No exercise coaching found after mapping', {
      recommendationsCount: result.recommendations?.length ?? 0,
      nextActionsCount: result.next_actions?.length ?? 0,
    });
  }

  return frontendResult;
}
